import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, DatePicker, Input, Radio, Select, Space, Steps, Tag } from 'antd';
import dayjs from 'dayjs';

import Guidance from '../common/Guidance';
import ToggleRow from '../common/ToggleRow';
import ConfirmWordModal from '../common/ConfirmWordModal';
import GroupHeader from '../common/GroupHeader';
import CategorySelect from '../discover/CategorySelect';
import { formatDisplayDate } from '../../utils/dateDisplay';
import {
	copyArchiveBack,
	fileCount,
	getReferrers,
	patchPublication,
	publicationRefusal,
	PUBLICATION_STATE_LABELS,
	retireOwnCollection,
	submitForReview,
	withdrawSubmission,
} from '../../services/publication';

const RETIRE_MIN_DAYS = 30;

function listingOf(publication, collection) {
	const listing = publication?.listing || {};
	return {
		title: listing.title ?? collection?.name ?? '',
		summary: listing.summary ?? (collection?.detail || collection?.description || ''),
		category: listing.category ?? '',
		tags: Array.isArray(listing.tags) ? listing.tags : [],
		sourceText: listing.source?.text ?? '',
		sourceUrl: listing.source?.url ?? '',
		licenseText: listing.license?.text ?? '',
		licenseUrl: listing.license?.url ?? '',
		disclaimer: listing.disclaimer ?? '',
	};
}

// An empty field is sent as null: a PATCH clears it, a submit stores nothing.
// A link with no name is not sent alone — the API refuses it, and the form
// says so before the round trip.
function attributionOf(text, url) {
	const t = text.trim();
	const u = url.trim();
	return t ? { text: t, ...(u ? { url: u } : {}) } : null;
}

/** Where the collection stands, as the three steps a reader of it would recognise. */
function Timeline({ publication }) {
	const state = publication?.state || null;
	const current = state === 'published' || state === 'retiring' ? 2 : state === 'changes-requested' ? 1 : state === 'submitted' ? 0 : -1;
	return (
		<Steps
			size="small"
			current={current}
			status={state === 'changes-requested' ? 'error' : undefined}
			className="publishing-tab__timeline"
			items={[
				{ title: 'Submitted', description: publication?.submittedAt ? formatDisplayDate(publication.submittedAt) : null },
				{ title: state === 'changes-requested' ? 'Changes requested' : 'Reviewed', description: publication?.reviewedAt ? formatDisplayDate(publication.reviewedAt) : null },
				{ title: state === 'retiring' ? 'Retiring' : 'Published', description: state === 'retiring' && publication?.retireAt ? `on ${formatDisplayDate(publication.retireAt)}` : (publication?.publishedAt ? formatDisplayDate(publication.publishedAt) : null) },
			]}
		/>
	);
}

/**
 * THE PUBLISHING TAB (docs/plans/personal-discover-global.md §4.7) — the
 * owner's side of a global collection, which its managers share (KPR-1267;
 * the owner is told when someone else submits it): where it stands, the listing every
 * account will read on Discover, the three switches (export, follow, and —
 * KPR-390 — readers saying "me too"), the
 * guidance on what publishing means, and the ONE action the state allows —
 * Submit for review · Withdraw · Retire…. A reviewer's note reads as a
 * quote. Managers only, saved collections only, never the personal one; its
 * endpoints apply immediately, outside the settings form's Save.
 */
export default function PublishingTab({ collection, collectionId }) {
	const { message } = App.useApp();
	const [publication, setPublication] = useState(collection?.publication || null);
	const [listing, setListing] = useState(() => listingOf(collection?.publication, collection));
	const [allowExport, setAllowExport] = useState(collection?.publication?.allowExport !== false);
	const [allowFollow, setAllowFollow] = useState(collection?.publication?.allowFollow !== false);
	// "Me too" from readers (KPR-390): OFF unless the owner turned it on —
	// unlike export and follow, it is the one write a reader would have, so
	// the default is the API's `allowConfirm: false`.
	const [allowConfirm, setAllowConfirm] = useState(collection?.publication?.allowConfirm === true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [retireOpen, setRetireOpen] = useState(false);
	const [retireAt, setRetireAt] = useState(null);
	const [retirePath, setRetirePath] = useState(null);
	const [referrers, setReferrers] = useState(null);
	const [copying, setCopying] = useState(null); // null | { copied, total }
	const [copyResult, setCopyResult] = useState(null);

	useEffect(() => {
		setPublication(collection?.publication || null);
		setListing(listingOf(collection?.publication, collection));
		setAllowExport(collection?.publication?.allowExport !== false);
		setAllowFollow(collection?.publication?.allowFollow !== false);
		setAllowConfirm(collection?.publication?.allowConfirm === true);
	}, [collection]);

	const state = publication?.state || null;
	const canSubmit = !state || state === 'changes-requested' || state === 'retired';
	const linkWithoutName = (!listing.sourceText.trim() && listing.sourceUrl.trim()) || (!listing.licenseText.trim() && listing.licenseUrl.trim());
	const listingValid = listing.title.trim() && listing.summary.trim() && !linkWithoutName;
	const archived = collection?.status === 'archived';

	const run = async (fn, done) => {
		setBusy(true);
		setError(null);
		try {
			const fresh = await fn();
			setPublication(fresh?.publication || null);
			if (done) message.success(done);
			return fresh;
		} catch (e) {
			setError(publicationRefusal(e));
			return null;
		} finally {
			setBusy(false);
		}
	};

	const body = () => ({
		listing: {
			title: listing.title.trim(),
			summary: listing.summary.trim(),
			category: listing.category.trim(),
			tags: listing.tags,
			source: attributionOf(listing.sourceText, listing.sourceUrl),
			license: attributionOf(listing.licenseText, listing.licenseUrl),
			disclaimer: listing.disclaimer.trim() || null,
		},
		allowExport,
		allowFollow,
		allowConfirm,
	});

	const openRetire = async () => {
		setRetireAt(dayjs().add(RETIRE_MIN_DAYS + 1, 'day'));
		setRetirePath(null);
		setRetireOpen(true);
		try {
			// The path is the owner's CHOICE: nothing is preselected; the
			// count and the server's suggestion are shown beside the options.
			setReferrers(await getReferrers(collectionId));
		} catch {
			setReferrers(null);
		}
	};

	const primary = canSubmit ? (
		<Button type="primary" loading={busy} disabled={!listingValid || archived} onClick={() => run(() => submitForReview(collectionId, body()), 'Submitted for review. A reviewer will look at it.')}>
			Submit for review
		</Button>
	) : (
		<Button type="primary" loading={busy} disabled={!listingValid} onClick={() => run(() => patchPublication(collectionId, body()), 'Listing saved.')}>
			Save listing
		</Button>
	);

	const earliest = useMemo(() => dayjs().add(RETIRE_MIN_DAYS, 'day').startOf('day'), []);

	// The way back from the archive path (delete audit F12): the records the
	// retirement moved out are copied back as new records, a batch at a time
	// (the API takes 500 a call and says how many remain). The archive keeps
	// the originals — they are what other collections' links point at.
	const archiveId = state === 'retired' ? publication?.archiveId || null : null;
	const copyBack = async () => {
		setError(null);
		setCopyResult(null);
		let copied = 0;
		// Each copy carries its record's files where this collection allows
		// attachments (keepr-api PR 10d); the rest stay with the originals.
		let carried = 0;
		let skipped = 0;
		let attachmentsOff = false;
		// A record whose copy was deleted is not copied again (the API counts
		// it over the whole archive, every call): the copy comes back from
		// Recently deleted, not as a second one beside it.
		let inRecentlyDeleted = 0;
		setCopying({ copied: 0, total: null });
		try {
			// Bounded: 40 batches is 20,000 records, the archive move's own cap.
			for (let round = 0; round < 40; round += 1) {
				// eslint-disable-next-line no-await-in-loop
				const report = await copyArchiveBack(collectionId);
				copied += report?.itemsCopied || 0;
				carried += report?.attachmentsCarried || 0;
				skipped += report?.attachmentsSkipped || 0;
				attachmentsOff = attachmentsOff || report?.attachmentsOff === true;
				inRecentlyDeleted = report?.copiesInRecentlyDeleted || 0;
				setCopying({ copied, total: report?.total ?? null });
				if (!report || !(report.remaining > 0)) break;
			}
			setCopyResult({ copied, carried, skipped, attachmentsOff, inRecentlyDeleted });
			message.success(copied === 1 ? '1 record copied back.' : `${copied} records copied back.`);
		} catch (e) {
			setError(publicationRefusal(e));
		} finally {
			setCopying(null);
		}
	};

	return (
		<div className="settings-column publishing-tab">
			<section className="form-panel">
				<GroupHeader as="h3" label="Global collection" />
				<div className="publishing-tab__state">
					<Tag>{state ? PUBLICATION_STATE_LABELS[state] || state : 'Not published'}</Tag>
					{state && state !== 'retired' ? <Timeline publication={publication} /> : null}
				</div>
				{publication?.reviewNote ? (
					<blockquote className="publishing-tab__note">
						<span className="micro-label">Reviewer&apos;s note</span>
						<p>{publication.reviewNote}</p>
					</blockquote>
				) : null}
				<Guidance
					tone="info"
					title="Every signed-in keepr account will be able to read it"
					why="Every item in this collection except private ones, its cards, and its changes the moment you make them. Other collections can link to its records, which is why a global collection retires with at least 30 days' notice instead of being deleted."
					links={['sharing-and-export/publish-a-collection-as-global', 'collections/follow-a-global-collection']}
				/>
				{archived ? <Alert type="warning" showIcon message="Unarchive the collection before submitting it." /> : null}
			</section>

			<section className="form-panel">
				<GroupHeader as="h3" label="Listing on Discover" />
				<label className="publishing-tab__field">
					<span className="micro-label">Title</span>
					<Input value={listing.title} maxLength={100} onChange={e => setListing(prev => ({ ...prev, title: e.target.value }))} aria-label="Listing title" />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">Summary</span>
					<Input.TextArea value={listing.summary} maxLength={500} autoSize={{ minRows: 2, maxRows: 6 }} onChange={e => setListing(prev => ({ ...prev, summary: e.target.value }))} aria-label="Listing summary" />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">Category</span>
					<CategorySelect value={listing.category} aria-label="Listing category" onChange={category => setListing(prev => ({ ...prev, category }))} />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">Tags</span>
					<Select mode="tags" value={listing.tags} maxCount={10} tokenSeparators={[',']} onChange={tags => setListing(prev => ({ ...prev, tags }))} aria-label="Listing tags" />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">Source</span>
					<Input value={listing.sourceText} maxLength={200} placeholder="Where the content comes from, e.g. FDA drug labels" onChange={e => setListing(prev => ({ ...prev, sourceText: e.target.value }))} aria-label="Source name" />
					<Input value={listing.sourceUrl} maxLength={500} placeholder="https://…" onChange={e => setListing(prev => ({ ...prev, sourceUrl: e.target.value }))} aria-label="Source link" />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">License</span>
					<Input value={listing.licenseText} maxLength={200} placeholder="The terms it is shared under, e.g. CC BY 4.0" onChange={e => setListing(prev => ({ ...prev, licenseText: e.target.value }))} aria-label="License name" />
					<Input value={listing.licenseUrl} maxLength={500} placeholder="https://…" onChange={e => setListing(prev => ({ ...prev, licenseUrl: e.target.value }))} aria-label="License link" />
				</label>
				<label className="publishing-tab__field">
					<span className="micro-label">Disclaimer</span>
					<Input.TextArea value={listing.disclaimer} maxLength={1000} showCount autoSize={{ minRows: 2, maxRows: 8 }} placeholder="What this collection is not, shown on the collection and on every record of it" onChange={e => setListing(prev => ({ ...prev, disclaimer: e.target.value }))} aria-label="Listing disclaimer" />
					<span className="meta-line">Plain text. Web addresses become links.</span>
				</label>
				{linkWithoutName ? <Alert type="warning" showIcon message="Give the source or license a name to go with its link." /> : null}
				{/* Colon-free ids: ToggleRow's useId fallback (`toggle-:r5:`) is
				    not a selector jsdom can parse, so a test asking for any
				    switch here by role threw before it found one. */}
				<ToggleRow first id="publishing-allow-export" label="Let readers export" description="Readers can export its records to PDF, Excel or JSON. People you share it with always can." checked={allowExport} onChange={setAllowExport} />
				<ToggleRow id="publishing-allow-follow" label="Let readers follow" description="Readers can follow it, which lists it with their own collections." checked={allowFollow} onChange={setAllowFollow} />
				<ToggleRow id="publishing-allow-confirm" label="Let readers say “me too”" description="Anyone who reads this collection can confirm its items that have a Me too element. You see who; they see the count." checked={allowConfirm} onChange={setAllowConfirm} />
			</section>

			{archiveId ? (
				<section className="form-panel">
					<GroupHeader as="h3" label="Records in the keepr archive" />
					<p>
						When it retired, the records other collections linked to left this collection for the keepr
						archive, so their links keep working. There they are read-only — for you too. Copy them back to
						have them here again as records you can edit; the archive keeps the originals.
					</p>
					<Button loading={Boolean(copying)} onClick={copyBack}>Copy archived records back</Button>
					{copying ? (
						<p className="meta-line" role="status">
							{copying.total ? `Copied ${copying.copied} of ${copying.total}…` : 'Copying…'}
						</p>
					) : null}
					{copyResult ? (
						<p className="meta-line" role="status">
							{copyResult.copied === 1 ? '1 record copied back' : `${copyResult.copied} records copied back`}
							{copyResult.carried ? `, with ${fileCount(copyResult.carried)}.` : '.'}
							{copyResult.skipped ? ` ${fileCount(copyResult.skipped)} ${copyResult.skipped === 1 ? 'stays' : 'stay'} with the original${copyResult.skipped === 1 ? '' : 's'} in the archive${copyResult.attachmentsOff ? ': attachments are off in this collection' : ''}.` : ''}
							{copyResult.inRecentlyDeleted ? ` ${copyResult.inRecentlyDeleted === 1 ? '1 record was' : `${copyResult.inRecentlyDeleted} records were`} not copied again: ${copyResult.inRecentlyDeleted === 1 ? 'its copy is' : 'their copies are'} in Settings › Recently deleted — restore ${copyResult.inRecentlyDeleted === 1 ? 'it' : 'them'} from there.` : ''}
						</p>
					) : null}
				</section>
			) : null}

			{error ? <Alert type="error" showIcon message={error} className="publishing-tab__error" /> : null}

			<Space wrap className="publishing-tab__actions">
				{state === 'submitted' ? (
					<Button loading={busy} onClick={() => run(() => withdrawSubmission(collectionId), 'Submission withdrawn.')}>Withdraw</Button>
				) : null}
				{state === 'published' ? (
					<Button danger onClick={openRetire}>Retire…</Button>
				) : null}
				{primary}
			</Space>

			<ConfirmWordModal
				open={retireOpen}
				word="retire"
				title="Retire this global collection"
				okText="Retire"
				extraValid={Boolean(retireAt && retirePath)}
				onCancel={() => setRetireOpen(false)}
				onOk={async () => {
					const fresh = await run(() => retireOwnCollection(collectionId, { retireAt: retireAt.toISOString(), path: retirePath }), `It retires on ${formatDisplayDate(retireAt.toDate())}.`);
					if (fresh) setRetireOpen(false);
				}}
			>
				<p>
					It stays readable until the date you choose — at least {RETIRE_MIN_DAYS} days away — and then only the people you share it with can read it.
				</p>
				<label className="publishing-tab__field">
					<span className="micro-label">Retire on</span>
					<DatePicker
						value={retireAt}
						onChange={setRetireAt}
						disabledDate={d => d && d.isBefore(earliest)}
						allowClear={false}
						aria-label="Retire on"
					/>
				</label>
				<div className="publishing-tab__field">
					<span className="micro-label">
						{referrers ? `${referrers.collections === 1 ? '1 collection links' : `${referrers.collections} collections link`} to its records` : 'Collections that link to it'}
					</span>
					{referrers?.suggestedPath ? (
						<span className="meta-line">Suggested: {referrers.suggestedPath === 'archive' ? 'the keepr archive' : 'ask them to copy'}.</span>
					) : null}
					<Radio.Group value={retirePath} onChange={e => setRetirePath(e.target.value)} aria-label="What happens to linked records">
						<Space direction="vertical">
							<Radio value="archive">Move the linked records to the keepr archive — every link keeps working and nobody else has to do anything, but those records leave this collection: in the archive they are read-only, for you too. You can copy them back afterwards.</Radio>
							<Radio value="copy">Ask the linking collections to copy what they link to before the date</Radio>
						</Space>
					</Radio.Group>
				</div>
			</ConfirmWordModal>
		</div>
	);
}
